"use client";
import { useCallback, useEffect, useState } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Lib ---
import {
  isExtensionAvailable,
  isNodeTypeSelected,
} from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { AlignCenterIcon } from "@/tiptap/icons/align-center-icon"
import { AlignJustifyIcon } from "@/tiptap/icons/align-justify-icon"
import { AlignLeftIcon } from "@/tiptap/icons/align-left-icon"
import { AlignRightIcon } from "@/tiptap/icons/align-right-icon";

export const TEXT_ALIGN_SHORTCUT_KEYS = {
  left: "mod+shift+l",
  center: "mod+shift+e",
  right: "mod+shift+r",
  justify: "mod+shift+j",
}

export const textAlignIcons = {
  left: AlignLeftIcon,
  center: AlignCenterIcon,
  right: AlignRightIcon,
  justify: AlignJustifyIcon,
}

export const textAlignLabels = {
  left: "Align left",
  center: "Align center",
  right: "Align right",
  justify: "Align justify",
}

/**
 * Checks if text alignment can be performed in the current editor state
 */
export function canSetTextAlign(editor, align) {
  if (!editor || !editor.isEditable) return false
  if (
    !isExtensionAvailable(editor, "textAlign") ||
    isNodeTypeSelected(editor, ["image", "horizontalRule"])
  )
    return false

  return editor.can().setTextAlign(align);
}

export function hasSetTextAlign(commands) {
  return "setTextAlign" in commands
}

function removeTextAlignStyle(style) {
  if (!style) return null

  const nextStyle = style
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .filter((declaration) => {
      const property = declaration.split(":")[0]?.trim().toLowerCase()
      return property !== "text-align"
    })
    .join("; ")

  return nextStyle || null
}

function updatePreservedTextAlign(editor, align) {
  const { state, view } = editor
  const { doc, selection } = state
  const tr = state.tr
  const seen = new Set()

  const updateNode = (node, pos) => {
    if (!node.isTextblock || seen.has(pos)) return
    seen.add(pos)

    const attrs = { ...node.attrs }
    let changed = false

    if (Object.prototype.hasOwnProperty.call(attrs, "style")) {
      attrs.style = removeTextAlignStyle(attrs.style)
      changed = true
    }

    if (Object.prototype.hasOwnProperty.call(attrs, "nodeTextAlign")) {
      attrs.nodeTextAlign = align
      changed = true
    }

    if (Object.prototype.hasOwnProperty.call(attrs, "textAlign")) {
      attrs.textAlign = align
      changed = true
    }

    if (changed) {
      tr.setNodeMarkup(pos, undefined, attrs, node.marks)
    }
  }

  if (selection.empty) {
    const { $from } = selection
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth)
      if (!node.isTextblock) continue

      updateNode(node, $from.before(depth))
      break
    }
  } else {
    doc.nodesBetween(selection.from, selection.to, updateNode)
  }

  if (tr.docChanged) {
    view.dispatch(tr)
  }
}

/**
 * Checks if the text alignment is currently active
 */
export function isTextAlignActive(editor, align) {
  if (!editor || !editor.isEditable) return false
  return editor.isActive({ textAlign: align });
}

/**
 * Sets text alignment in the editor
 */
export function setTextAlign(editor, align) {
  if (!editor || !editor.isEditable) return false
  if (!canSetTextAlign(editor, align)) return false

  const chain = editor.chain().focus()
  if (hasSetTextAlign(chain)) {
    const success = chain.setTextAlign(align).run()
    if (success) {
      updatePreservedTextAlign(editor, align)
    }
    return success;
  }

  return false
}

/**
 * Determines if the text align button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable, align } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isExtensionAvailable(editor, "textAlign")) return false

  if (!editor.isActive("code")) {
    return canSetTextAlign(editor, align);
  }

  return true
}

/**
 * Custom hook that provides text align functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MySimpleAlignButton() {
 *   const { isVisible, handleTextAlign } = useTextAlign({ align: "center" })
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleTextAlign}>Align Center</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedAlignButton() {
 *   const { isVisible, handleTextAlign, label, isActive } = useTextAlign({
 *     editor: myEditor,
 *     align: "right",
 *     hideWhenUnavailable: true,
 *     onAligned: () => console.log('Text aligned!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleTextAlign}
 *       aria-pressed={isActive}
 *       aria-label={label}
 *     >
 *       Align Right
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useTextAlign(config) {
  const {
    editor: providedEditor,
    align,
    hideWhenUnavailable = false,
    onAligned,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canAlign = canSetTextAlign(editor, align)
  const isActive = isTextAlignActive(editor, align)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, align, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable, align])

  const handleTextAlign = useCallback(() => {
    if (!editor) return false

    const success = setTextAlign(editor, align)
    if (success) {
      onAligned?.()
    }
    return success
  }, [editor, align, onAligned])

  return {
    isVisible,
    isActive,
    handleTextAlign,
    canAlign,
    label: textAlignLabels[align],
    shortcutKeys: TEXT_ALIGN_SHORTCUT_KEYS[align],
    Icon: textAlignIcons[align],
  }
}
